/**
 * @fileoverview Optimization panel — finds the weakest room, derives the lowest
 * sufficient supply temperature and per-room flows/presets to meet every room's
 * heat demand, applies them, and shows a step-by-step report.
 *
 * @module HydraulicBalancingTesting/components/OptimizationPanel
 */

import React, { useState, useMemo } from 'react';
import { Button, Table, Tag, Alert, Timeline, Tooltip } from 'antd';
import { ThunderboltOutlined, InfoCircleOutlined, AimOutlined } from '@ant-design/icons';

import {
  steadyHeatDemand,
  requiredMeanRadiatorTemp,
  runOptimization,
  toLitersPerHour,
} from '../physics';
import { HEAT_PUMP, PRESET_POSITIONS } from '../constants';

import styles from '../HydraulicBalancingTesting.module.css';

const etaColor = (eta) => (eta >= 1.0 ? '#52c41a' : eta >= 0.85 ? '#faad14' : '#ff4d4f');

export const OptimizationPanel = ({ engine, sandbox, onOpenTheory }) => {
  const { rooms, params } = engine;
  const [report, setReport] = useState(null);

  // --- live (cheap) analysis -------------------------------------------------
  const analysis = useMemo(() => {
    const demand = steadyHeatDemand(rooms, params);
    const spread = HEAT_PUMP.DESIGN_SPREAD;
    const per = rooms.map((room, i) => {
      const d = demand[i];
      const tAvgReq = requiredMeanRadiatorTemp(d, room.radiatorNominal, room.Tset);
      return {
        key: room.id,
        idx: i,
        name: room.name,
        eta: d > 1 ? room.qrad / d : 1,
        demand: d,
        tAvgReq,
        tSupReq: tAvgReq + spread / 2,
        designFlowLph: toLitersPerHour(room.designFlow),
        preset: PRESET_POSITIONS[room.presetIndex].label,
      };
    });
    let weakestIdx = 0;
    per.forEach((p, i) => { if (p.eta < per[weakestIdx].eta) weakestIdx = i; });
    const rawSupply = Math.max(...per.map((p) => p.tSupReq));
    return { per, weakestIdx, rawSupply, capped: rawSupply > HEAT_PUMP.MAX_SUPPLY + 0.05 };
  }, [rooms, params]);

  const weakest = analysis.per[analysis.weakestIdx];

  const handleOptimize = () => {
    const result = runOptimization(rooms, params);
    engine.applyOptimization({ tSupply: result.tSupply, presets: result.presets });
    setReport(result);
  };

  // --- analysis table --------------------------------------------------------
  const analysisColumns = [
    { title: 'Room', dataIndex: 'name', key: 'name', render: (v, r) => <span>R{r.idx + 1} {v}</span> },
    {
      title: <Tooltip title="η = current radiator output ÷ heat demand. Below 1 the radiator can't keep up.">η</Tooltip>,
      key: 'eta',
      align: 'right',
      render: (_, r) => <b style={{ color: etaColor(r.eta) }}>{r.eta.toFixed(2)}</b>,
    },
    { title: 'Demand', key: 'demand', align: 'right', render: (_, r) => `${r.demand.toFixed(0)} W` },
    { title: 'Req. Tavg', key: 'tavg', align: 'right', render: (_, r) => `${r.tAvgReq.toFixed(1)} °C` },
    {
      title: 'Req. supply',
      key: 'tsup',
      align: 'right',
      render: (_, r) => {
        const over = r.tSupReq > HEAT_PUMP.MAX_SUPPLY + 0.05;
        return <span style={over ? { color: '#ff4d4f' } : undefined}>{r.tSupReq.toFixed(1)} °C</span>;
      },
    },
    { title: 'Design flow', key: 'flow', align: 'right', render: (_, r) => `${r.designFlowLph.toFixed(0)} l/h` },
    { title: 'Preset', key: 'preset', align: 'center', render: (_, r) => r.preset },
  ];

  return (
    <div className={styles.panelCard}>
      <div className={styles.roomHeaderRow}>
        <p className={styles.panelTitle} style={{ margin: 0 }}>
          <AimOutlined /> Optimization
        </p>
        <InfoCircleOutlined
          onClick={onOpenTheory}
          style={{ color: '#1890ff', cursor: 'pointer', fontSize: 16 }}
          title="How does the optimizer work?"
        />
      </div>
      <p className={styles.optIntro}>
        Meets every room's heat demand at the lowest sufficient supply temperature (best COP) by
        sizing the supply to the weakest room and setting each valve preset to the required flow.
      </p>

      <Alert
        type={weakest ? (analysis.capped ? 'warning' : 'info') : 'info'}
        showIcon
        style={{ marginBottom: 12 }}
        message={
          weakest
            ? `Weakest room: ${weakest.name} — coverage η ${weakest.eta.toFixed(2)} (lowest). System supply ≈ ${analysis.rawSupply.toFixed(1)} °C`
            : 'No rooms'
        }
        description={
          analysis.capped
            ? `The most-demanding room needs more than the heat pump's ${HEAT_PUMP.MAX_SUPPLY} °C limit — its radiator is undersized; supply will be capped and the room flagged.`
            : 'Optimization sets the lowest supply temperature that covers every room and balances each branch to its design flow.'
        }
      />

      <Table size="small" columns={analysisColumns} dataSource={analysis.per} pagination={false}
        rowClassName={(r) => (r.idx === analysis.weakestIdx ? styles.optWeakestRow : '')} />

      <Tooltip title={sandbox ? '' : 'Optimization changes supply temperature & presets — available in the sandbox (exit the mission first).'}>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={handleOptimize}
          disabled={!sandbox}
          style={{ marginTop: 14 }}
          block
        >
          Optimize supply temperature & presets
        </Button>
      </Tooltip>

      {report && <OptimizationReport report={report} rooms={rooms} />}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Report (steps + before/after + per-room changes)
// ----------------------------------------------------------------------------

const OptimizationReport = ({ report, rooms }) => {
  const { steps, before, after, perRoom, undersized, pumpLimited } = report;

  const sysRows = [
    { key: 'sup', metric: 'Supply temperature', before: `${before.tSupply.toFixed(1)} °C`, after: `${after.tSupply.toFixed(1)} °C` },
    { key: 'cop', metric: 'COP', before: before.cop.toFixed(2), after: after.cop.toFixed(2) },
    { key: 'bal', metric: 'Balance score', before: before.balance, after: after.balance },
    { key: 'eff', metric: 'Efficiency score', before: before.efficiency, after: after.efficiency },
  ];
  const sysColumns = [
    { title: 'Metric', dataIndex: 'metric', key: 'm' },
    { title: 'Before', dataIndex: 'before', key: 'b', align: 'right' },
    { title: 'After', dataIndex: 'after', key: 'a', align: 'right', render: (v) => <b>{v}</b> },
  ];

  const roomColumns = [
    { title: 'Room', key: 'name', render: (_, r) => <span>R{r.idx + 1} {rooms[r.idx].name}</span> },
    {
      title: 'Preset',
      key: 'preset',
      align: 'center',
      render: (_, r) => `${PRESET_POSITIONS[r.presetBefore].label} → ${PRESET_POSITIONS[r.presetAfter].label}`,
    },
    {
      title: 'Flow (l/h)',
      key: 'flow',
      align: 'right',
      render: (_, r) => `${toLitersPerHour(r.flowBefore).toFixed(0)} → ${toLitersPerHour(r.flowAfter).toFixed(0)}`,
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      render: (_, r) => {
        if (undersized.includes(r.idx)) return <Tag color="error">supply capped</Tag>;
        if (pumpLimited.includes(r.idx)) return <Tag color="warning">pump-limited</Tag>;
        return <Tag color="success">ok</Tag>;
      },
    },
  ];

  return (
    <div className={styles.optReport}>
      <p className={styles.sectionLabel} style={{ marginTop: 16 }}>Steps taken</p>
      <Timeline
        items={steps.map((s) => ({
          color: s.startsWith('⚠') ? 'red' : 'blue',
          children: <span style={s.startsWith('⚠') ? { color: '#d4380d' } : undefined}>{s}</span>,
        }))}
      />

      <p className={styles.sectionLabel}>System — before vs after</p>
      <Table size="small" columns={sysColumns} dataSource={sysRows} pagination={false} />

      <p className={styles.sectionLabel} style={{ marginTop: 14 }}>Per-room changes</p>
      <Table size="small" columns={roomColumns} dataSource={perRoom.map((p) => ({ ...p, key: p.id }))} pagination={false} />

      <Alert
        type="success"
        showIcon
        style={{ marginTop: 12 }}
        message="Applied"
        description="Supply temperature and presets are set. Comfort scores rise as the rooms settle — press Run and let the simulation advance."
      />
    </div>
  );
};
